import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import "firebase/compat/firestore";
import "firebase/compat/storage";

async function createReportRequest(inspectionId, reportType) {
  const createReportFn = firebase.functions().httpsCallable("createReport");

  const result = await createReportFn({
    inspectionId: inspectionId,
    reportType: reportType,
  });

  return result.data.data;
}

/**
 * Requests one report combining several inspections. The callable verifies the
 * caller can read every inspection, so this only passes the ids along.
 *
 * @param {Array<string>} inspectionIds
 * @param {string} reportType "pdf" or "xlsx"
 * @return {Promise<string>} path of the request document to watch
 */
async function createCombinedReportRequest(inspectionIds, reportType) {
  const createCombinedReportFn = firebase
    .functions()
    .httpsCallable("createCombinedReport");

  const result = await createCombinedReportFn({
    inspectionIds: inspectionIds,
    reportType: reportType,
  });

  return result.data.data;
}

function listenToRequestStatus(requestPath, onChange, onError) {
  return firebase
    .firestore()
    .doc(requestPath)
    .onSnapshot((doc) => {
      const data = doc.data();

      // The document can arrive empty if it is removed while being watched.
      if (!data) return;

      if (data.status == "error") {
        onError(new Error(data.error ?? "Report generation failed."));
        return;
      }

      onChange(data.status, requestPath);
    }, onError);
}

async function getDownloadURL(requestPath) {
  const snap = await firebase.firestore().doc(requestPath).get();

  if (!snap.exists) {
    throw new Error("Report request does not exist.");
  }

  const data = snap.data();

  if (data.status !== "done") {
    throw new Error("Report is not finished generating.");
  }

  if (!data.storagePath) {
    throw new Error("Request does not have a storage path.");
  }

  return await firebase
    .storage()
    .ref()
    .child(data.storagePath)
    .getDownloadURL();
}

export {
  createReportRequest,
  createCombinedReportRequest,
  listenToRequestStatus,
  getDownloadURL,
};
